import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle } from 'lucide-react';

const needsOptions = [
  'Custom AI',
  'Web Development',
  'E-Commerce',
  'App Creation',
  'Blockchain',
  'Web3',
  'NFTs',
  'E-Book Publishing',
  'Other',
];

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xldjgqko';

interface FormFields {
  firstname: string;
  email: string;
  message: string;
  needs: string[];
}

const ContactForm: React.FC = () => {
  const [fields, setFields] = useState<FormFields>({ firstname: '', email: '', message: '', needs: [] });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFields(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCheckbox = (value: string) => {
    setFields(prev => ({
      ...prev,
      needs: prev.needs.includes(value) ? prev.needs.filter(n => n !== value) : [...prev.needs, value],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...fields, needs: fields.needs.join('; ') }),
      });
      if (res.ok) {
        setStatus('success');
        setFields({ firstname: '', email: '', message: '', needs: [] });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        style={{
          background: 'var(--bg-card)',
          border: '1px solid rgba(109, 74, 255, 0.3)',
          borderRadius: '16px',
          padding: '48px 32px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <CheckCircle size={48} color="var(--accent-purple-light)" />
        <h3 style={{ fontSize: '22px', fontWeight: 700 }}>Message Sent!</h3>
        <p style={{ color: 'var(--text-secondary)', maxWidth: '320px' }}>
          Thanks for reaching out. We'll get back to you shortly.
        </p>
        <button
          className="btn btn-outline"
          onClick={() => setStatus('idle')}
          style={{ marginTop: '8px' }}
        >
          Send Another
        </button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5 }}
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-card)',
        borderRadius: '16px',
        padding: 'clamp(24px, 4vw, 40px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
      }}
    >
      {/* Name + Email row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        <div className="form-field">
          <label htmlFor="cf-firstname">First Name *</label>
          <input
            id="cf-firstname"
            type="text"
            name="firstname"
            value={fields.firstname}
            onChange={handleChange}
            placeholder="Your name"
            required
          />
        </div>
        <div className="form-field">
          <label htmlFor="cf-email">Email Address *</label>
          <input
            id="cf-email"
            type="email"
            name="email"
            value={fields.email}
            onChange={handleChange}
            placeholder="hello@yourcompany.com"
            required
          />
        </div>
      </div>

      {/* Needs checkboxes */}
      <div>
        <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '12px' }}>
          What do you need? *
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '8px',
        }}>
          {needsOptions.map(option => {
            const checked = fields.needs.includes(option);
            return (
              <label
                key={option}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${checked ? 'var(--accent-purple)' : 'var(--border-card)'}`,
                  background: checked ? 'rgba(109, 74, 255, 0.1)' : 'rgba(255,255,255,0.02)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontSize: '13px',
                  fontWeight: 500,
                  color: checked ? 'var(--accent-purple-light)' : 'var(--text-secondary)',
                  userSelect: 'none',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleCheckbox(option)}
                  style={{ display: 'none' }}
                />
                <span style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '4px',
                  border: `1.5px solid ${checked ? 'var(--accent-purple)' : 'rgba(255,255,255,0.2)'}`,
                  background: checked ? 'var(--accent-purple)' : 'transparent',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s',
                }}>
                  {checked && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                {option}
              </label>
            );
          })}
        </div>
      </div>

      {/* Message */}
      <div className="form-field">
        <label htmlFor="cf-message">Message *</label>
        <textarea
          id="cf-message"
          name="message"
          value={fields.message}
          onChange={handleChange}
          placeholder="Tell us about your project..."
          required
          rows={4}
          style={{ resize: 'vertical', minHeight: '120px' }}
        />
      </div>

      {/* Error */}
      {status === 'error' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#fca5a5',
        }}>
          <AlertCircle size={16} />
          Something went wrong. Please try again or email us directly.
        </div>
      )}

      {/* Submit */}
      <motion.button
        type="submit"
        disabled={status === 'sending'}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="btn btn-primary"
        style={{
          alignSelf: 'flex-start',
          opacity: status === 'sending' ? 0.7 : 1,
          cursor: status === 'sending' ? 'not-allowed' : 'pointer',
        }}
      >
        {status === 'sending' ? (
          <>
            <div style={{
              width: '16px', height: '16px', borderRadius: '50%',
              border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white',
              animation: 'spin-ring 0.7s linear infinite',
            }} />
            Sending...
          </>
        ) : (
          <>
            <Send size={16} />
            Send Message
          </>
        )}
      </motion.button>
    </motion.form>
  );
};

export default ContactForm;
